import React from 'react';
import {
  Eye,
  Grid3X3,
  Box,
  Crosshair,
  Zap,
  BarChart2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

export const DebugOptions: React.FC = () => {
  const {
    debugOverlay,
    debugRender,
    updateDebugOverlay,
    updateDebugRender,
    toggleDebugOverlay,
  } = useLivePreviewStore();

  const ToggleButton: React.FC<{
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    shortcut?: string;
  }> = ({ active, onClick, icon, label, shortcut }) => (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded text-sm w-full',
        active ? 'bg-blue-600/20 text-blue-400' : 'hover:bg-zinc-700'
      )}
      title={shortcut}
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      {shortcut && (
        <span className="text-xs text-zinc-500">{shortcut}</span>
      )}
    </button>
  );

  return (
    <div className="p-3 space-y-4 max-h-96 overflow-y-auto">
      {/* Stats Overlay */}
      <div>
        <h4 className="text-sm font-medium text-zinc-400 mb-2">Stats Overlay</h4>
        <div className="space-y-1">
          <ToggleButton
            active={debugOverlay.enabled}
            onClick={toggleDebugOverlay}
            icon={<BarChart2 size={16} />}
            label="Show Stats Overlay"
            shortcut="F3"
          />

          {debugOverlay.enabled && (
            <div className="ml-6 mt-2 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'showFps', label: 'FPS' },
                  { key: 'showFrameTime', label: 'Frame Time' },
                  { key: 'showDrawCalls', label: 'Draw Calls' },
                  { key: 'showObjectCount', label: 'Objects' },
                  { key: 'showMemory', label: 'Memory' },
                  { key: 'showTextures', label: 'Textures' },
                  { key: 'showAudio', label: 'Audio' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={debugOverlay[key as keyof typeof debugOverlay] as boolean}
                      onChange={(e) =>
                        updateDebugOverlay({ [key]: e.target.checked })
                      }
                      className="w-3 h-3 rounded bg-zinc-700"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-2">
                <label className="text-xs text-zinc-400">Position:</label>
                <select
                  value={debugOverlay.position}
                  onChange={(e) =>
                    updateDebugOverlay({
                      position: e.target.value as typeof debugOverlay.position,
                    })
                  }
                  className="flex-1 px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-xs"
                >
                  <option value="top-left">Top Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="bottom-right">Bottom Right</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-zinc-400">Opacity:</label>
                <input
                  type="range"
                  min="0.3"
                  max="1"
                  step="0.1"
                  value={debugOverlay.opacity}
                  onChange={(e) =>
                    updateDebugOverlay({ opacity: parseFloat(e.target.value) })
                  }
                  className="flex-1"
                />
                <span className="text-xs w-8">{Math.round(debugOverlay.opacity * 100)}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Render Debug */}
      <div>
        <h4 className="text-sm font-medium text-zinc-400 mb-2">Render Debug</h4>
        <div className="space-y-1">
          <ToggleButton
            active={debugRender.showColliders}
            onClick={() => updateDebugRender({ showColliders: !debugRender.showColliders })}
            icon={<Box size={16} />}
            label="Show Colliders"
            shortcut="F4"
          />

          <ToggleButton
            active={debugRender.showGrid}
            onClick={() => updateDebugRender({ showGrid: !debugRender.showGrid })}
            icon={<Grid3X3 size={16} />}
            label="Show Grid"
            shortcut="F5"
          />

          <ToggleButton
            active={debugRender.showBounds}
            onClick={() => updateDebugRender({ showBounds: !debugRender.showBounds })}
            icon={<Eye size={16} />}
            label="Show Bounds"
            shortcut="F6"
          />

          <ToggleButton
            active={debugRender.showOrigins}
            onClick={() => updateDebugRender({ showOrigins: !debugRender.showOrigins })}
            icon={<Crosshair size={16} />}
            label="Show Origins"
            shortcut="F7"
          />
        </div>
      </div>

      {/* Performance */}
      <div>
        <h4 className="text-sm font-medium text-zinc-400 mb-2">Performance</h4>
        <div className="space-y-1">
          <ToggleButton
            active={debugRender.lowLatencyMode}
            onClick={() => updateDebugRender({ lowLatencyMode: !debugRender.lowLatencyMode })}
            icon={<Zap size={16} />}
            label="Low Latency Mode"
          />
        </div>
      </div>
    </div>
  );
};
